import { PropertyValueMap, TemplateResult, html, nothing, svg, CSSResultGroup } from 'lit';
import { eventOptions, property, query, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { customConverter } from '../../util/custom-converter';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import { BlocksCarouselItem } from '../carousel-item/carousel-item';
import '../icon/icons/arrow-back';
import '../icon/icons/arrow-forward';
import '../icon/icons/chevron-left';
import '../icon/icons/chevron-right';
import '../icon/icons/pause';
import '../icon/icons/play-arrow';
import '../skeleton/skeleton';

import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksSlideChangeEvent } from '../../events';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import type { CarouselBehavior, CarouselNavMode } from './carousel.model';
import styles from './carousel.styles';

/**
 * @slot previous-icon - The component previous button icon slot
 * @slot next-icon - The component next button icon slot
 * @slot play - The component play button icon slot
 * @slot pause - The component pause button icon slot
 * @slot caption - The component caption content
 * @slot default - The component default content
 *
 * @fires bl-slide-change - emitted when carousel slide changes
 *
 * @csspart base - The component's base wrapper.
 * @csspart footer - The component's footer container.
 * @csspart slideCount - The component's slider count and caption container.
 * @csspart carousel-item-text - The component's carousel item label and role.
 * @csspart slideNumber - The component's slide number text
 * @csspart currentSlide - The component's current slide
 * @csspart delimiter - The component's count  delimiter
 * @csspart totalSlide - The component's total count
 * @csspart part-caption - The component's caption text
 * @csspart controls - The component's body controls wrapper.
 * @csspart autoplay-button - The component's autoplay button.
 * @csspart part-play - The component's play slot.
 * @csspart play-arrow - The component's play-arrow-icon.
 * @csspart part-pause - The component's pause slot.
 * @csspart pause-icon - The component's pause icon.
 * @csspart wrapper - The component's wrapper.
 * @csspart container - The component's container.
 * @csspart part-default - The component's default slot.
 * @csspart external-buttons - The component's external navigation buttons.
 * @csspart previous-button - The component's previous button.
 * @csspart part-previous-icon - The component's previous-icon slot.
 * @csspart chevron-left - The component's chevron-left icon.
 * @csspart arrow-back - The component's arrow-back icon.
 * @csspart next-button - The component's next button.
 * @csspart part-next-icon - The component's next-icon slot.
 * @csspart chevron-right - The component's chevron-right icon.
 * @csspart arrow-forward - The component's arrow-forward icon.
 * @csspart progress-container - The component's progress container for overflow variant.
 * @csspart progress-dot - The component's progress dots.
 * @csspart progress-dot-circle - The component's progress dot circles.
 * @csspart base-loader - The component's loader base.
 * @csspart body-loader - The component's body loader.
 * @csspart body-skeleton - The component's body skeleton.
 * @csspart footer-loader - The component's footer loader.
 * @csspart caption-skeleton - The component's caption loader.
 * @csspart controls-loader - The component's controls loader.
 * @csspart pause-button-loader - The component's pause button loader.
 * @csspart pause-icon-loader - The component's pause icon loader.
 * @csspart previous-button-loader - The component's previous icon loader.
 * @csspart arrow-back-loader - The component's arrow back loader.
 * @csspart next-button-loader - The component's next button loader.
 * @csspart arrow-forward-loader - The component's arrow forward loader.
 * 
 * @cssproperty --cat-carousel-slide-gap - The space between each slide.
 * @cssproperty [--cat-carousel-aspect-ratio=16/9] - The aspect ratio of each slide.
 * @cssproperty --cat-carousel-scroll-hint-start - The amount of padding to apply to left of the scroll area, allowing previous slides to become
 *  partially visible as a scroll hint.
 * @cssproperty --cat-carousel-scroll-hint-end - The amount of padding to apply to right of the scroll area, allowing next slides to become
 *  partially visible as a scroll hint.

 */
export class BlocksCarousel extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Boolean to determine whether to enable autoplay. By default, autoplay is disabled
   */
  @property({ type: Boolean })
  autoplay = false;

  /**
   * Amount of time in milliseconds which determines the interval between each automatic scrolling
   */
  @property({ type: Number })
  autoplayInterval = 2000;

  /**
   * Determines the type of navigation.
   * `external` - displays navigation buttons outside the carousel container. Default is `external`.
   */
  private navigation = 'external';

  /**
   * Determines the mode of the navigation buttons
   */
  @property({ type: String })
  navigationMode: CarouselNavMode = 'light';

  /**
   * Determines the behavior of the carousel
   * `slideShow` - displays one carousel item at a time
   * `overflow` - used to display the previous and next carousel items by a specified padding.
   * `--cat-carousel-scroll-hint-start` and `--cat-carousel-scroll-hint-end` css custom variables will be applied default as 10%
   * when `overflow` behavior is used
   * Default behavior is `slideShow`
   */
  @property({ type: String, reflect: true })
  behavior: CarouselBehavior = 'slideShow';

  /**
   * Number to determine the active carousel item on load
   * Default is `0`
   */
  @property({ type: Number })
  activeIndex = 0;

  /**
   * Boolean to determine whether to hide the slide count or not
   * Default is false
   */
  @property({ type: Boolean })
  hideSlideCount = false;

  /**
   * Boolean to determine whether to enable mouse dragging in the carousel slides
   */
  @property({ type: Boolean })
  mouseDragging = false;

  /**
   * Boolean to determine the loading state.
   * Default is false
   */
  @property({ type: Boolean })
  isLoading = false;

  /**
   * Aria label attribute, To be used only for accessibility
   * Provides an accessible name for the carousel element
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * Aria role description attribute, To be used only for accessibility
   * To define a localized and human-readable description for a role
   * If the aria-roledescription property is set to “carousel”,
   * we should not set the aria-label property to “carousel” because this would be redundant.
   */
  @property({ type: String })
  ariaroledescription = 'carousel';

  /**
   * Pause button text, To be used only for accessibility
   */
  @property({ type: String })
  pauseButtonText = 'Stop automatic slide show';

  /**
   * Play button text, To be used only for accessibility
   */
  @property({ type: String })
  playButtonText = 'Play automatic slide show';

  /**
   * Previous slide button text, To be used only for accessibility
   */
  @property({ type: String })
  previousButtonText = 'Previous slide';

  /**
   * Next slide button text, To be used only for accessibility
   */
  @property({ type: String })
  nextButtonText = 'Next slide';

  /**
   * Breakpoint below which carousel switches to mobile view. Defaults to 768px.
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * Boolean to determine whether to loop through the slides. Default is `true`. If it's set to `false`,
   * the prev button on first slide and next button on last slide will be disabled respectively.
   */
  @property({
    // Allow "true|false" attribute values but keep the property boolean
    type: Boolean,
    converter: customConverter()
  })
  loop = true;

  /**
   * Indicates the active slide
   */
  @state()
  private activeSlide = 0;

  /**
   * Indicates if scrolling is done currently
   */
  @state()
  private scrolling = false;

  /**
   * Indicates if mouse dragging is done currently
   */
  @state()
  private dragging = false;

  /**
   * Indicates if autoplay is paused
   */
  @state()
  private pauseAutoplay = false;

  /**
   * Indicates the slide number currently
   */
  @state()
  private slideNumber = 0;

  /**
   * Stores the current slide caption text
   */
  @state()
  private activeSlideCaption = '';

  /**
   * Number to determine how many slides display at a given time. Default is `1`
   * Cannot be less than 1
   */
  @state()
  private slidesPerPage = 1;

  /**
   * Number to determine how many slides advance when scrolled at a time. Useful when specifying a `slidesPerPage`
   * greater than one. It can't be higher than `slidesPerPage`. Default is `1`
   * Cannot be less than 1
   */
  @state()
  private slidesPerMove = 1;

  /**
   * Boolean used to hide the progress container which displays for `overflow` behavior
   */
  @state()
  private hideProgress = false;

  /**
   * Boolean used to track mobile view
   */
  @state()
  private isMobile = false;

  @state()
  private activeSlideLabel = '';
  /**
   * returns the carousel container
   */
  @query('.cat-c-carousel-container')
  private carouselContainer: HTMLElement | undefined;

  private mutationObserver: MutationObserver | undefined;
  private clearSetTimeoutScroll: ReturnType<typeof setTimeout> | undefined;
  private timerId = 0;
  private isFocussed = false;
  private isHovered = false;
  private firstCloneDone = false;

  /**
   * returns whether the node is a carousel item
   */
  private isCarouselItem(node: Node): node is BlocksCarouselItem {
    return node instanceof Element && node.tagName.toLowerCase() === 'cat-carousel-item';
  }

  /** @internal Gets all carousel items. */
  private getCarouselItems({ excludeClones = true }: { excludeClones?: boolean } = {}): BlocksCarouselItem[] {
    return Array.from(this.children).filter(
      (el: Node) => this.isCarouselItem(el) && (!excludeClones || !el.hasAttribute('data-clone'))
    ) as BlocksCarouselItem[];
  }

  /**
   * returns the page count based on slidesPerMove and SlidePerPage. Used in canScrollNext()
   */
  private getPageCount(): number {
    const slidesCount = this.getCarouselItems().length;
    const { slidesPerPage, slidesPerMove, loop } = this;

    const pages = loop ? slidesCount / slidesPerMove : (slidesCount - slidesPerPage) / slidesPerMove + 1;

    return Math.ceil(pages);
  }

  /**
   * returns the current page number based on slidesPerMove.
   */
  private getCurrentPage(): number {
    return Math.ceil(this.activeSlide / this.slidesPerMove);
  }

  /**
   * Determines whether we can scroll to next item
   */
  private canScrollNext(): boolean {
    return this.loop || this.getCurrentPage() < this.getPageCount() - 1;
  }

  /**
   * Determines whether we can scroll to previous item
   */
  private canScrollPrevious(): boolean {
    return this.loop || this.getCurrentPage() > 0;
  }

  /**
   * Sets isMobile whenever the screen is in mobile view
   */
  private triggerSmallScreen = () => {
    this.isMobile = window.innerWidth < this.mobileBreakPoint;
  };

  private get getCurrentCarouselSlide(): BlocksCarouselItem {
    return this.getCarouselItems()?.[this.activeSlide];
  }

  /**
   * First updated lifecycle hook
   * 1) Initialize the slides
   * 2) Create a mutation observer to determine if any carousel item has been added or deleted and reinitialize slides based on it
   */
  protected firstUpdated(): void {
    this.configureSlides(); /* 1 */
    this.mutationObserver = new MutationObserver(this.handleSlotChange); /* 2 */
    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true
    });
  }

  /**
   * will update lifecycle hook
   */
  protected willUpdate(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('slidesPerPage') && this.slidesPerPage <= 0) {
      this.slidesPerPage = 1;
    }
    if (_changedProperties.has('slidesPerMove') && this.slidesPerMove <= 0) {
      this.slidesPerMove = 1;
    }
  }

  /**
   * updated lifecycle hook
   */
  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('slidesPerPage') || _changedProperties.has('loop')) {
      this.configureSlides();
    }
    if (_changedProperties.has('slidesPerMove')) {
      this.updateSlidesSnap();
    }
    if (_changedProperties.has('activeSlide')) {
      this.handelSlideChange();
    }
    if (_changedProperties.has('autoplay')) {
      this.handleAutoplayChange();
    }
    if ((_changedProperties.has('activeIndex') || _changedProperties.has('isLoading')) && !this.isLoading) {
      this.goToSlide(this.activeIndex);
    }
  }

  /**
   * Connected lifecycle hook
   */
  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('resize', this.triggerSmallScreen);
    this.triggerSmallScreen();
  }

  /**
   * disconnected lifecycle hook
   */
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.mutationObserver?.disconnect();
    window.removeEventListener('resize', this.triggerSmallScreen);
  }

  @eventOptions({ passive: true })
  private handleFocusIn(): void {
    this.isFocussed = true;
  }

  private handleFocusOut(): void {
    this.isFocussed = false;
  }

  private handleMouseEnter(): void {
    this.isHovered = true;
  }

  private handleMouseLeave(): void {
    this.isHovered = false;
  }

  private start(): void {
    this.stop();

    this.timerId = window.setInterval(() => {
      if (!this.isFocussed && !this.isHovered) {
        this.nextSlide();
      }
    }, this.autoplayInterval);
  }

  private stop(): void {
    clearInterval(this.timerId);
  }

  /**
   * Called when `autoplay` property changes
   */
  private handleAutoplayChange(): void {
    this.stop();
    if (this.autoplay) {
      this.start();
    }
  }

  /**
   * To handle autoplay
   */
  private handleAutoplay(): void {
    this.pauseAutoplay = !this.pauseAutoplay;
    if (this.pauseAutoplay) {
      this.stop();
    } else {
      this.start();
    }
  }

  /**
   * Called when `activeSlide` state changes.
   * 1) Used to toggle `cat-carousel-is-active` class based on active slide
   * 2) Dispatch `bl-slide-change` on slide change
   */
  private handelSlideChange(): void {
    const slides = this.getCarouselItems();
    slides.forEach((slide, i) => {
      slide.classList.toggle('cat-carousel-is-active', i === this.activeSlide); /* 1 */
    });

    if (this.hasUpdated) {
      this.dispatch({ eventName: 'bl-slide-change', detailObj: { index: this.activeSlide } }) as BlocksSlideChangeEvent; /* 2 */
    }
  }

  /**
   * Called when dom changes
   * 1) Reinitialize the carousel if a carousel item has been added or removed
   */
  private handleSlotChange = (mutations: MutationRecord[]): void => {
    const needsInitialization = mutations.some((mutation) => {
      const addedNodesArray = Array.from(mutation.addedNodes);
      const removedNodesArray = Array.from(mutation.removedNodes);

      return (
        addedNodesArray.some((el: Node) => this.isCarouselItem(el) && !el.hasAttribute('data-clone')) ||
        removedNodesArray.some((el: Node) => this.isCarouselItem(el) && !el.hasAttribute('data-clone'))
      );
    });

    if (needsInitialization) {
      /* 1 */
      this.configureSlides();
    }
    // Requesting update manually since no reactive property is changed
    this.requestUpdate();
  };

  /**
   * Initialize carousel slides
   * 1) Removes all the cloned elements from the carousel
   * 2) Creates clones to be placed before and after the original elements to simulate infinite scrolling
   * 3) Because the DOM may be changed, restore the scroll position to the active slide
   */
  private configureSlides(): void {
    this.getCarouselItems({ excludeClones: false }).forEach((slide) => {
      slide.classList.remove('cat-carousel-in-view');
      slide.classList.remove('cat-carousel-is-active');

      if (slide.hasAttribute('data-clone')) {
        slide.remove(); /* 1 */
      }
    });
    this.updateSlidesSnap();
    if (this.loop && this.firstCloneDone) {
      this.cloneSlides(); /* 2 */
    }
    this.alignSlides();
    if (!this.isLoading) {
      this.goToSlide(this.activeSlide, 'auto'); /* 3 */
    }
  }

  /**
   * Updates whether `scroll-snap-align` to be set to `none`.
   * Useful in scenarios when `slidesPerMove` is greater than 1
   * 1) https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-align
   * `scroll-snap-align` is `start` as default for every slides.
   * This is useful when we want to skip some carousel slides while scrolling
   */
  private updateSlidesSnap(): void {
    const slides = this.getCarouselItems();

    slides.forEach((slide, i) => {
      const shouldSnap = (i + this.slidesPerMove) % this.slidesPerMove === 0;
      if (shouldSnap) {
        slide.style.removeProperty('scroll-snap-align');
      } else {
        slide.style.setProperty('scroll-snap-align', 'none'); /* 1 */
      }
    });
  }

  /**
   * Create clones of the last and pervious slides when `loop` is true
   * 1) Create clone of the last slides at the start of the array
   * 2) Create clone of the first slides at the end of the array
   */
  private cloneSlides(): void {
    const slides = this.getCarouselItems();

    const lastSlides = slides.slice(-this.slidesPerPage);
    const firstSlides = slides.slice(0, this.slidesPerPage);

    lastSlides.reverse().forEach((slide, i) => {
      const clone = slide.cloneNode(true) as HTMLElement;
      clone.setAttribute('data-clone', String(slides.length - i - 1));
      this.prepend(clone); /* 1 */
    });

    firstSlides.forEach((slide, i) => {
      const clone = slide.cloneNode(true) as HTMLElement;
      clone.setAttribute('data-clone', String(i));
      this.append(clone); /* 2 */
    });
  }

  /**
   * Called when mouse drag is done
   * 1) Drag will happen only when we use the mouse left-button.
   * https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
   */
  private handleMouseDragStart(event: PointerEvent): void {
    const canDrag = event.button === 0; /* 1 */
    if (canDrag) {
      event.preventDefault();

      document.addEventListener('pointermove', this.handleMouseDrag, { capture: true, passive: true });
      document.addEventListener('pointerup', this.handleMouseDragEnd, { capture: true, once: true });
    }
  }

  /**
   * Called when mouse drag
   * 1) Start dragging if it hasn't yet
   */
  private handleMouseDrag = (event: PointerEvent): void => {
    if (!this.dragging) {
      this.carouselContainer?.style.setProperty('scroll-snap-type', 'none'); /* 1 */
      this.dragging = true;
    }

    this.carouselContainer?.scrollBy({
      left: -event.movementX,
      behavior: 'instant'
    });
  };

  /**
   * Called when mouse drag is stopped
   * 1) get the current scroll position
   * 2) remove the scroll-snap-type property so that the browser will snap the slide to the correct position
   * 3) forcing a style recalculation doesn't seem to immediately update the scroll position in Safari.\
   *  Setting "overflow" to "hidden" should force this behavior.
   * 4) get the final scroll position to the slide snapped by the browser
   * 5) Restore the scroll position to the original one, so that it can be smoothly animated
   * 6) The mouseDrag can be ended in step 4 itself. But, to provide smooth experience to the user
   * we are storing both initial scroll and final scroll position and scroll to the final position is done
   * only after a tick inside requestAnimationFrame
   */
  private handleMouseDragEnd = (): void => {
    const scrollContainer = this.carouselContainer;

    document.removeEventListener('pointermove', this.handleMouseDrag, { capture: true });

    const startLeft = scrollContainer?.scrollLeft; /* 1 */

    scrollContainer?.style.removeProperty('scroll-snap-type'); /* 2 */

    scrollContainer?.style.setProperty('overflow', 'hidden'); /* 3 */

    const finalLeft = scrollContainer?.scrollLeft; /* 4 */

    scrollContainer?.style.removeProperty('overflow'); /* 5 */
    scrollContainer?.style.setProperty('scroll-snap-type', 'none');
    scrollContainer?.scrollTo({ left: startLeft, behavior: 'instant' });

    requestAnimationFrame(() => {
      if (startLeft !== finalLeft) {
        scrollContainer?.scrollTo({
          /* 6 */ left: finalLeft,
          behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
        });
      }

      scrollContainer?.style.removeProperty('scroll-snap-type');

      this.dragging = false;
      this.handleScrollEnd();
    });
  };

  /** Tells if the user has enabled the "reduced motion" setting in their browser or OS. */
  private prefersReducedMotion(): boolean {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    return query.matches;
  }

  /**
   * Handles keydown on carousel container
   */
  private handleKeyDown(event: KeyboardEvent): void {
    if (
      [KEYS.LEFT_ARROW, KEYS.RIGHT_ARROW, KEYS.UP_ARROW, KEYS.DOWN_ARROW, KEYS.HOME, KEYS.END].includes(event.key) &&
      event.target === this.carouselContainer
    ) {
      const isNext = event.key === KEYS.DOWN_ARROW || event.key === KEYS.RIGHT_ARROW;
      const isPrevious = event.key === KEYS.UP_ARROW || event.key === KEYS.LEFT_ARROW;

      event.preventDefault();

      if (isPrevious) {
        this.previousSlide();
      }

      if (isNext) {
        this.nextSlide();
      }

      if (event.key === 'Home') {
        this.goToSlide(0);
      }

      if (event.key === 'End') {
        this.goToSlide(this.getCarouselItems().length - 1);
      }
    }
  }

  /**
   * Creates the initial clone of slides if the loop property is true
   */
  private createInitialClones(): void {
    if (this.loop && !this.firstCloneDone) {
      this.cloneSlides();
      this.firstCloneDone = true;
    }
  }

  /** @internal Synchronizes the slides with the IntersectionObserver API.
   * 1) Scrolls to the original slide without animating, so the user won't notice that the position has changed
   * 2) Update the current index based on the first visible slide
   * 3) Set the index to the first "snappable" slide
   *
   */
  private alignSlides = (): void => {
    const changeSlide = (entries: IntersectionObserverEntry[]) => {
      intersectionObserver.disconnect();

      for (const entry of entries) {
        const slide = entry.target;
        slide.toggleAttribute('inert', !entry.isIntersecting || slide.hasAttribute('data-clone'));
        slide.classList.toggle('cat-carousel-in-view', entry.isIntersecting);
        slide.setAttribute('aria-hidden', entry.isIntersecting && !slide.hasAttribute('data-clone') ? 'false' : 'true');
      }

      const firstIntersecting = entries.find((entry) => entry.isIntersecting);

      if (firstIntersecting) {
        if (this.loop && firstIntersecting.target.hasAttribute('data-clone')) {
          const clonePosition = Number(firstIntersecting.target.getAttribute('data-clone'));
          this.goToSlide(clonePosition, 'instant'); /* 1 */
        } else {
          const slides = this.getCarouselItems();
          const slideIndex = slides.indexOf(firstIntersecting.target as BlocksCarouselItem); /* 2 */
          this.activeSlide = Math.ceil(slideIndex / this.slidesPerMove) * this.slidesPerMove; /* 3 */
          this.activeSlideLabel = `${this.getCurrentCarouselSlide?.arialabel} ${this.getCurrentCarouselSlide?.ariaroledescription}`;
          this.activeSlideCaption = this.getCurrentCarouselSlide?.captionText;
          this.slideNumber = this.activeSlide;
        }
      }
    };
    const intersectionObserver = new IntersectionObserver(changeSlide, {
      root: this.carouselContainer,
      threshold: 0.6
    });

    this.getCarouselItems({ excludeClones: false }).forEach((slide) => {
      intersectionObserver.observe(slide);
    });
  };

  /**
   * Move the carousel backward by `slides-per-move` slides.
   *
   * @param behavior - The behavior used for scrolling.
   */
  async previousSlide(behavior: ScrollBehavior = 'smooth'): Promise<void> {
    this.createInitialClones();
    await this.updateComplete;
    this.goToSlide(this.activeSlide - this.slidesPerMove, behavior);
  }

  /**
   * Move the carousel forward by `slides-per-move` slides.
   *
   * @param behavior - The behavior used for scrolling.
   */
  async nextSlide(behavior: ScrollBehavior = 'smooth'): Promise<void> {
    this.createInitialClones();
    await this.updateComplete;
    if (!this.isLoading) {
      this.goToSlide(this.activeSlide + this.slidesPerMove, behavior);
    }
  }

  /**
   * Identifies the carousel item we need to scroll
   * 1) No need to do anything in case there are no items in the carousel
   * 2) Sets the next index without taking into account clones, if any
   * 3) Get the index of the next slide. For looping carousel it adds `slidesPerPage`
   * to normalize the starting index in order to ignore the first nth clones.
   *
   * @param index - The slide number where carousel container needs to scroll
   */
  goToSlide(index: number, behavior: ScrollBehavior = 'smooth'): void {
    const slides = this.getCarouselItems();
    const slidesWithClones = this.getCarouselItems({ excludeClones: false });

    if (!slides.length) {
      return; /* 1 */
    }

    const newActiveSlide =
      this.loop && this.firstCloneDone ? (index + slides.length) % slides.length : this.minMax(index, 0, slides.length - 1); /* 2 */
    this.activeSlide = newActiveSlide;

    const nextSlideIndex = this.minMax(index + (this.loop && this.firstCloneDone ? this.slidesPerPage : 0), 0, slidesWithClones.length - 1); /* 3 */
    const nextSlide = slidesWithClones[nextSlideIndex];

    this.scrollToSlide(nextSlide, this.prefersReducedMotion() ? 'auto' : behavior);
  }

  /**
   * Scrolls the carousel container to the active slide
   * 1) Updates the active slide caption text
   * 2) Updates the active slide number
   */
  private async scrollToSlide(slide: HTMLElement, behavior: ScrollBehavior = 'smooth'): Promise<void> {
    await this.updateComplete;
    const scrollContainer = this.carouselContainer;
    const scrollContainerRect = scrollContainer?.getBoundingClientRect();
    const nextSlideRect = slide.getBoundingClientRect();

    const nextLeft = nextSlideRect.left - (scrollContainerRect?.left ?? 0);
    const nextTop = nextSlideRect.top - (scrollContainerRect?.top ?? 0);

    scrollContainer?.scrollTo({
      left: nextLeft + scrollContainer?.scrollLeft,
      top: nextTop + scrollContainer?.scrollTop,
      behavior
    });
  }

  /**
   * Ensures a number stays within a minimum and maximum value
   * Useful to stay between carousel item array and not access negative index or length greater than slides length
   */
  private minMax(value: number, min: number, max: number): number {
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  }

  /**
   * Called when scrolling starts
   * @TODO Used debounce to handle when scrolling finishes.
   * This can be achieved using native `scrollend` event, but scrollend is not supported by safari yet.
   * https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollend_event
   *
   */
  @eventOptions({ passive: true })
  private handleScroll(): void {
    this.scrolling = true;
    this.createInitialClones();
    if (this.clearSetTimeoutScroll) {
      clearTimeout(this.clearSetTimeoutScroll);
    }
    this.clearSetTimeoutScroll = setTimeout(() => {
      clearTimeout(this.clearSetTimeoutScroll);
      this.handleScrollEnd();
    }, 200);
  }

  /**
   * Called when scrolling ends
   */
  private handleScrollEnd(): void {
    if (!this.scrolling || this.dragging) return;

    this.alignSlides();

    this.scrolling = false;
  }

  private renderLoading(componentClassNames: DirectiveResult<typeof ClassMapDirective>): TemplateResult<1> {
    return html`
      <div part="base-loader" class="${componentClassNames}">
        <div part="body-loader">
          <cat-skeleton part="body-skeleton" exportparts=${this.getExportParts('skeleton-body', 'skeleton')} class="cat-c-carousel-body-skeleton">
          </cat-skeleton>
        </div>
        <div part="footer-loader" class="cat-c-carousel-footer-loader">
          <cat-skeleton
            part="caption-skeleton"
            exportparts=${this.getExportParts('skeleton-caption', 'skeleton')}
            size="sm"
            class="cat-c-carousel-caption-skeleton"
          >
          </cat-skeleton>
          <div class="cat-c-carousel-controls-loader">
            ${this.autoplay
              ? html`<div class="cat-c-carousel-autoplay-loader">
                  <cat-button
                    part="pause-button-loader"
                    exportparts=${this.getExportParts('skeleton-pause', 'button')}
                    hideText
                    variant="ghost"
                    class="cat-c-carousel-pause-button cat-c-carousel-autoplay"
                    ?disabled=${true}
                    ><cat-icon-pause
                      part="pause-icon-loader"
                      exportparts=${this.getExportParts('skeleton-pause', 'icon')}
                      slot="after"
                    ></cat-icon-pause
                    >${this.pauseButtonText}
                  </cat-button>
                  <svg class="cat-c-carousel-divider-loader" xmlns="http://www.w3.org/2000/svg" width="2" height="16" viewBox="0 0 2 18" fill="none">
                    <path d="M1 1L0.999999 17" stroke="#CCCCCC" stroke-linecap="round" />
                  </svg>
                </div>`
              : nothing}
            <div class="cat-c-carousel-arrows-loader">
              <cat-button
                part="previous-button-loader"
                exportparts=${this.getExportParts('skeleton-previous', 'button')}
                hideText
                variant="ghost"
                ?disabled=${true}
              >
                <cat-icon-arrow-back
                  part="arrow-back-loader"
                  exportparts=${this.getExportParts('skeleton-arrow-back', 'icon')}
                  class="cat-c-carousel-loading-arrow-back"
                  slot="after"
                ></cat-icon-arrow-back
                >${this.previousButtonText}</cat-button
              >
              <cat-button
                part="next-button-loader"
                exportparts=${this.getExportParts('skeleton-next', 'button')}
                hideText
                variant="ghost"
                ?disabled=${true}
                ><cat-icon-arrow-forward
                  part="arrow-forward-loader"
                  exportparts=${this.getExportParts('skeleton-arrow-forward', 'icon')}
                  class="cat-c-carousel-loading-arrow-forward"
                  slot="after"
                ></cat-icon-arrow-forward
                >${this.nextButtonText}</cat-button
              >
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Renders the navigation buttons to navigate between carousel slides
   */
  private renderNavigators(): TemplateResult<1> {
    return html`${this.renderPreviousNavigator()} ${this.renderNextNavigator()}`;
  }

  /**
   * Renders the previous navigation button to navigate to previous carousel slide
   */
  private renderPreviousNavigator(): TemplateResult<1> {
    return html`<cat-button
      part="previous-button"
      exportparts=${this.getExportParts('previous', 'button')}
      hideText
      variant="ghost"
      @click=${this.canScrollPrevious() && !this.isLoading ? () => this.previousSlide() : nothing}
      ?disabled=${!(this.canScrollPrevious() && !this.isLoading)}
      label=${this.previousButtonText}
      ><slot part="part-previous-icon" name="previous-icon" slot="after"
        ><cat-icon-arrow-back
          part="arrow-back"
          exportparts=${this.getExportParts('arrow-back', 'icon')}
          class="cat-c-carousel-arrow-back"
        ></cat-icon-arrow-back></slot
    ></cat-button>`;
  }

  /**
   * Renders the next navigation button to navigate to next carousel slide
   */
  private renderNextNavigator(): TemplateResult<1> {
    return html` <cat-button
      part="next-button"
      exportparts=${this.getExportParts('next', 'button')}
      hideText
      variant="ghost"
      @click=${this.canScrollNext() && !this.isLoading ? () => this.nextSlide() : nothing}
      ?disabled=${!(this.canScrollNext() && !this.isLoading)}
      label=${this.nextButtonText}
      ><slot part="part-next-icon" name="next-icon" slot="after"
        ><cat-icon-arrow-forward
          part="arrow-forward"
          exportparts=${this.getExportParts('arrow-forward', 'icon')}
          class="cat-c-carousel-arrow-forward"
        ></cat-icon-arrow-forward></slot
    ></cat-button>`;
  }

  /**
   * Renders the autoplay pause/play button
   */
  private renderAutoPlay(): TemplateResult<1> {
    return html`<cat-button
      part="autoplay-button"
      exportparts=${this.getExportParts('autoplay', 'button')}
      hideText
      variant="ghost"
      @click=${this.handleAutoplay}
      class="cat-c-carousel-autoplay"
      label=${this.pauseAutoplay ? this.playButtonText : this.pauseButtonText}
      >${this.pauseAutoplay
        ? html`<slot part="part-play" name="play" slot="after"
            ><cat-icon-play-arrow
              part="play-arrow"
              exportparts=${this.getExportParts('play-arrow', 'icon')}
              class="cat-c-carousel-play-arrow"
            ></cat-icon-play-arrow
          ></slot> `
        : html`<slot part="part-pause" name="pause" slot="after"
            ><cat-icon-pause part="pause-icon" exportparts=${this.getExportParts('pause', 'icon')} class="cat-c-carousel-pause-icon"></cat-icon-pause
          ></slot>`}
    </cat-button>`;
  }

  /**
   * Renders the progress container to display when behavior is `overflow`
   */
  private renderPagination(): TemplateResult<1> {
    return html`<div part="progress-container" class="cat-c-carousel-progress-container">
      ${this.getCarouselItems().map(
        (_, index) => svg`<svg part="progress-dot" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle part="progress-dot-circle" opacity="${index === this.slideNumber ? nothing : '0.3'}" cx="12" cy="12" r="6" fill=${
            this.navigationMode === 'dark' ? 'white' : 'black'
          } />
        </svg>`
      )}
    </div>`;
  }

  render(): TemplateResult<1> {
    const isSlideShow = this.behavior === 'slideShow';
    const isOverflow = this.behavior === 'overflow';
    const componentClassNames = this.componentClassNames('cat-c-carousel', {
      'cat-c-carousel--navigation-dark': this.navigationMode === 'dark',
      'cat-c-carousel-navigation-external': this.navigation === 'external',
      'cat-c-carousel-autoplay': this.autoplay === true,
      'cat-c-carousel--behavior-slideShow': isSlideShow,
      'cat-c-carousel--behavior-overflow': isOverflow,
      'cat-c-carousel--loading': this.isLoading === true,
      'cat-c-carousel--is-mobile': this.isMobile
    });

    if (this.isLoading) {
      return this.renderLoading(componentClassNames);
    }
    return html` <div
      part="base"
      class="${componentClassNames}"
      style="--cat-carousel-slides-per-page: ${this.slidesPerPage};"
      role="region"
      aria-label="${ifDefined(this.arialabel)}"
      aria-roledescription="${this.ariaroledescription}"
    >
      <div part="footer" class="cat-c-carousel-footer">
        ${this.autoplay ? html`<div part="controls" class="cat-c-carousel-controls">${this.renderAutoPlay()}</div>` : nothing}
      </div>
      <div
        part="wrapper"
        class="cat-c-carousel-wrapper"
        @focusin=${this.handleFocusIn}
        @focusout=${this.handleFocusOut}
        @mouseenter=${this.handleMouseEnter}
        @mouseleave=${this.handleMouseLeave}
        @touchstart=${this.handleFocusIn}
        @touchend=${(this, this.handleFocusOut)}
      >
        <div
          part="container"
          class="cat-c-carousel-container"
          @mousedown="${this.mouseDragging ? this.handleMouseDragStart : nothing}"
          @scroll="${this.handleScroll}"
          @keydown=${this.handleKeyDown}
          tabindex=${0}
          aria-live="polite"
        >
          <slot part="part-default"></slot>
        </div>
      </div>
      <div part="slideCount" class="cat-c-carousel--slide-count" aria-live="polite">
        <div part="carousel-item-text" class="cat-u-is-vishidden">${this.activeSlideLabel}</div>
        ${!this.hideSlideCount && isSlideShow
          ? html`<div part="slideNumber" class="cat-c-carousel-slideNumber" aria-hidden="true">
              <span part="currentSlide">${this.slideNumber + 1}</span><span part="delimiter">/</span
              ><span part="totalSlide">${this.getCarouselItems().length}</span>
            </div>`
          : nothing}
        <div part="part-caption" class="cat-c-carousel-caption ${isOverflow ? 'cat-u-is-vishidden' : ''}">
          <slot name="caption">${this.activeSlideCaption}</slot>
        </div>
      </div>
      ${isOverflow && !this.hideProgress ? this.renderPagination() : nothing}
      <div
        part="external-buttons"
        class="cat-c-carousel-navigation-external-buttons"
        @focusin=${this.handleFocusIn}
        @focusout=${this.handleFocusOut}
        @mouseenter=${this.handleMouseEnter}
        @mouseleave=${this.handleMouseLeave}
        @touchstart=${this.handleFocusIn}
        @touchend=${(this, this.handleFocusOut)}
      >
        ${this.renderNavigators()}
      </div>
    </div>`;
  }
}

if (customElements.get('cat-carousel') === undefined) {
  customElements.define('cat-carousel', BlocksCarousel);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-carousel': BlocksCarousel;
  }
}
